import { useEffect, useRef, useState } from "react";
import { Check, Copy, Share2, Mail, X as CloseIcon } from "lucide-react";
import { toast } from "sonner";

const SHARE_URL = "https://wemail.xyz";
const SHARE_TEXT =
  "I just reserved my @w.xyz address — the next generation of email. Reserve yours:";

const XIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor">
    <path d="M18.244 2H21.5l-7.5 8.575L23 22h-6.844l-5.36-6.59L4.6 22H1.34l8.018-9.166L1 2h7.014l4.85 6.07L18.244 2Zm-2.4 18h1.892L7.25 4h-2L15.844 20Z" />
  </svg>
);

const FacebookIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor">
    <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.19 8.44 9.94v-7.03H7.9v-2.9h2.54V9.85c0-2.52 1.5-3.9 3.77-3.9 1.1 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.78-1.63 1.57v1.88h2.78l-.44 2.9h-2.34V22c4.78-.75 8.44-4.92 8.44-9.94Z"/>
  </svg>
);

const LinkedInIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor">
    <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.34V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.38-1.85 3.62 0 4.29 2.38 4.29 5.48v6.26ZM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13ZM7.12 20.45H3.56V9h3.56v11.45ZM22.22 0H1.77C.79 0 0 .77 0 1.72v20.55C0 23.23.79 24 1.77 24h20.44c.98 0 1.79-.77 1.79-1.73V1.72C24 .77 23.2 0 22.22 0Z"/>
  </svg>
);

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor">
    <path d="M17.5 14.4c-.28-.14-1.66-.82-1.91-.91-.26-.1-.44-.14-.63.14-.19.28-.72.9-.88 1.08-.16.19-.32.21-.6.07-.28-.14-1.19-.44-2.26-1.4-.83-.75-1.4-1.67-1.56-1.95-.16-.28-.02-.43.13-.57.13-.13.28-.32.42-.48.14-.16.19-.28.28-.47.09-.19.05-.35-.02-.49-.07-.14-.63-1.52-.87-2.09-.23-.55-.46-.47-.63-.48-.16-.01-.35-.01-.53-.01-.19 0-.49.07-.75.35-.26.28-.98.96-.98 2.34 0 1.38 1 2.71 1.14 2.9.14.19 1.98 3.02 4.79 4.24.67.29 1.19.46 1.6.59.67.21 1.28.18 1.76.11.54-.08 1.66-.68 1.89-1.34.23-.66.23-1.22.16-1.34-.07-.12-.26-.19-.54-.33ZM12.05 21.53h-.01c-1.75 0-3.47-.47-4.97-1.36l-.36-.21-3.7.97.99-3.6-.23-.37a9.87 9.87 0 0 1-1.51-5.26c0-5.46 4.44-9.9 9.9-9.9 2.64 0 5.13 1.03 6.99 2.9a9.83 9.83 0 0 1 2.89 6.99c-.01 5.46-4.45 9.9-9.9 9.9Zm8.42-18.31A11.81 11.81 0 0 0 12.04 0C5.46 0 .1 5.36.1 11.94c0 2.1.55 4.15 1.59 5.96L0 24l6.24-1.64a11.9 11.9 0 0 0 5.79 1.47h.01c6.58 0 11.94-5.36 11.94-11.94a11.87 11.87 0 0 0-3.51-8.47Z"/>
  </svg>
);

const TelegramIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor">
    <path d="M11.94 15.19 11.61 19.6c.47 0 .68-.2.93-.44l2.23-2.13 4.62 3.38c.85.47 1.45.22 1.68-.78L23.94 3.9c.27-1.24-.45-1.72-1.28-1.42L1.03 10.71c-1.22.48-1.2 1.16-.2 1.47l5.55 1.73L19.24 6c.6-.4 1.15-.18.7.22"/>
  </svg>
);

const ShareIconButton = ({ children, label, onClick, href, testid, active = false }) => {
  const className = `group inline-flex items-center justify-center w-9 h-9 rounded-full border transition-colors ${
    active
      ? "border-bone/40 text-pearl"
      : "border-hairline text-ash hover:text-pearl hover:border-bone/40"
  }`;
  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
        data-testid={testid}
        className={className}
      >
        {children}
      </a>
    );
  }
  return (
    <button
      type="button"
      aria-label={label}
      data-testid={testid}
      onClick={onClick}
      className={className}
    >
      {children}
    </button>
  );
};

const ShareOption = ({ href, onClick, icon: Icon, label, testid }) => {
  const cls =
    "flex items-center gap-3 w-full px-3 py-2 rounded-md text-left text-[13px] text-ash hover:text-pearl hover:bg-[#4A8DB8]/[0.04] transition-colors";
  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testid}
        className={cls}
      >
        <Icon className="w-4 h-4 shrink-0" />
        {label}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} data-testid={testid} className={cls}>
      <Icon className="w-4 h-4 shrink-0" />
      {label}
    </button>
  );
};

export const ShareRow = ({ context = "share", className = "", overline = "Share W" }) => {
  const [copied, setCopied] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Close menu on outside click / Escape
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_URL);
      setCopied(true);
      toast.success("Link copied.");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy link.");
    }
  };

  const handleShareClick = async () => {
    // Mobile: use the OS share sheet.
    if (navigator.share) {
      try {
        await navigator.share({ title: "W — For The Win", text: SHARE_TEXT, url: SHARE_URL });
        return;
      } catch (err) {
        if (err && err.name === "AbortError") return;
        // fall through to popover
      }
    }
    // Desktop: toggle the popover.
    setMenuOpen((v) => !v);
  };

  const encodedUrl = encodeURIComponent(SHARE_URL);
  const encodedText = encodeURIComponent(SHARE_TEXT);
  const xUrl = `https://x.com/intent/post?text=${encodedText}&url=${encodedUrl}`;
  const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
  const liUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
  const waUrl = `https://wa.me/?text=${encodeURIComponent(`${SHARE_TEXT} ${SHARE_URL}`)}`;
  const tgUrl = `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`;
  const mailUrl = `mailto:?subject=${encodeURIComponent(
    "You should see this — @w.xyz"
  )}&body=${encodeURIComponent(`${SHARE_TEXT} ${SHARE_URL}`)}`;

  return (
    <div
      ref={wrapperRef}
      data-testid={`share-row-${context}`}
      className={`relative flex items-center gap-3 ${className}`}
    >
      <span className="label-overline">{overline}</span>
      <div className="flex items-center gap-2">
        <ShareIconButton label="Share on X" href={xUrl} testid={`share-${context}-x`}>
          <XIcon className="w-3.5 h-3.5" />
        </ShareIconButton>
        <ShareIconButton
          label="Copy link"
          onClick={handleCopy}
          testid={`share-${context}-copy`}
        >
          {copied ? (
            <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
          ) : (
            <Copy className="w-3.5 h-3.5" />
          )}
        </ShareIconButton>
        <ShareIconButton
          label="More share options"
          onClick={handleShareClick}
          testid={`share-${context}-native`}
          active={menuOpen}
        >
          <Share2 className="w-3.5 h-3.5" />
        </ShareIconButton>
      </div>

      {menuOpen && (
        <div
          data-testid={`share-${context}-menu`}
          className="absolute z-50 top-full mt-2 right-0 w-56 rounded-lg border border-[var(--w-border)] bg-[var(--w-card)] p-1.5 shadow-lg"
          role="menu"
        >
          <div className="flex items-center justify-between px-2 py-1.5 mb-1 border-b border-[var(--w-border)]">
            <span className="label-overline text-ash/70">Share via</span>
            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              aria-label="Close share menu"
              data-testid={`share-${context}-menu-close`}
              className="text-ash hover:text-pearl transition-colors"
            >
              <CloseIcon className="w-3.5 h-3.5" />
            </button>
          </div>
          <ShareOption
            href={fbUrl}
            icon={FacebookIcon}
            label="Facebook"
            testid={`share-${context}-facebook`}
          />
          <ShareOption
            href={liUrl}
            icon={LinkedInIcon}
            label="LinkedIn"
            testid={`share-${context}-linkedin`}
          />
          <ShareOption
            href={waUrl}
            icon={WhatsAppIcon}
            label="WhatsApp"
            testid={`share-${context}-whatsapp`}
          />
          <ShareOption
            href={tgUrl}
            icon={TelegramIcon}
            label="Telegram"
            testid={`share-${context}-telegram`}
          />
          <ShareOption
            href={mailUrl}
            icon={Mail}
            label="Email"
            testid={`share-${context}-email`}
          />
        </div>
      )}
    </div>
  );
};

export default ShareRow;
